import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { ArrowLeft, Plug, CheckCircle2, XCircle, AlertCircle, Loader2, ExternalLink, RefreshCw } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  connected: { label: "Connected", color: "text-emerald-300 bg-emerald-500/10 border-emerald-500/30", icon: CheckCircle2 },
  pending_connection: { label: "Pending Connection", color: "text-amber-300 bg-amber-500/10 border-amber-500/30", icon: AlertCircle },
  error: { label: "Error", color: "text-rose-300 bg-rose-500/10 border-rose-500/30", icon: XCircle },
  disabled: { label: "Disabled", color: "text-white/50 bg-white/5 border-white/10", icon: XCircle },
};

export default function IntegrationCenterPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [testingKey, setTestingKey] = useState(null);
  const [openConfig, setOpenConfig] = useState(null);
  const [credsInput, setCredsInput] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/integrations/status`, { withCredentials: true });
      setItems(data.integrations || []);
    } catch (e) {
      if (e.response?.status === 401) navigate("/admin/login");
      else toast.error("Load failed");
    } finally { setLoading(false); }
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  const testConnection = async (key) => {
    setTestingKey(key);
    try {
      const { data } = await axios.post(`${API}/api/integrations/${key}/test`, {}, { withCredentials: true });
      toast[data.result?.ok ? "success" : "warning"](data.result?.message || "Test done");
      load();
    } catch (e) {
      toast.error("Test failed");
    } finally { setTestingKey(null); }
  };

  const saveConfig = async () => {
    if (!openConfig) return;
    try {
      await axios.post(
        `${API}/api/integrations/${openConfig.key}/configure`,
        { credentials: credsInput, enabled: true },
        { withCredentials: true }
      );
      toast.success("Credentials saved");
      setOpenConfig(null);
      setCredsInput({});
      load();
    } catch (e) {
      toast.error("Save failed");
    }
  };

  const toggleEnabled = async (key, newEnabled) => {
    try {
      await axios.post(
        `${API}/api/integrations/${key}/configure`,
        { enabled: newEnabled },
        { withCredentials: true }
      );
      toast.success(newEnabled ? "Enabled" : "Disabled");
      load();
    } catch (e) {
      toast.error("Update failed");
    }
  };

  const grouped = items.reduce((acc, it) => {
    acc[it.category] = acc[it.category] || [];
    acc[it.category].push(it);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-[#0F172A] text-white pb-24" data-testid="integration-center-page">
      <header className="border-b border-gold/20 bg-[#0a1120]/60 backdrop-blur">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-3">
          <button onClick={() => navigate("/admin/dashboard")} className="flex items-center gap-2 text-gold/80 hover:text-gold text-sm" data-testid="btn-back-to-dashboard">
            <ArrowLeft className="w-4 h-4" /> Admin Dashboard
          </button>
          <div className="flex items-center gap-2">
            <Plug className="w-5 h-5 text-gold" />
            <h1 className="text-lg sm:text-xl font-heading text-gold">Integration Center</h1>
          </div>
          <button onClick={load} className="text-gold/80 hover:text-gold" data-testid="btn-refresh">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        {Object.entries(grouped).map(([cat, list]) => (
          <section key={cat} data-testid={`integration-category-${cat}`}>
            <h2 className="text-base font-heading text-gold/90 mb-3 uppercase tracking-wide">
              {cat === "search_analytics" ? "Search & Analytics" :
               cat === "trend_data" ? "Trend Data" :
               cat === "astrology_data" ? "Astrology Data Sources" : cat}
            </h2>
            <div className="grid md:grid-cols-2 gap-3">
              {list.map((it) => {
                const meta = STATUS_META[it.status] || STATUS_META.pending_connection;
                const Icon = meta.icon;
                return (
                  <div key={it.key} className="rounded-2xl border border-gold/25 bg-[#0a1120]/60 p-4"
                       data-testid={`integration-card-${it.key}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <h3 className="font-semibold text-white truncate">{it.display_name_hi}</h3>
                        <p className="text-xs text-white/50">{it.display_name_en}</p>
                      </div>
                      <span className={`text-[11px] px-2 py-1 rounded-full border flex items-center gap-1 shrink-0 ${meta.color}`}>
                        <Icon className="w-3 h-3" /> {meta.label}
                      </span>
                    </div>

                    <p className="mt-3 text-[13px] text-white/70 leading-relaxed">{it.credentials_help_hi}</p>

                    {it.required_credentials?.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-1">
                        {it.required_credentials.map((k) => (
                          <span key={k} className={`px-2 py-0.5 rounded-full text-[10px] border ${
                            it.credentials_provided?.[k]
                              ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/30"
                              : "bg-white/5 text-white/50 border-white/10"
                          }`}>
                            {it.credentials_provided?.[k] ? "✓" : "○"} {k}
                          </span>
                        ))}
                      </div>
                    )}

                    <div className="mt-3 text-[11px] text-white/50 space-y-1">
                      {it.last_test_at && (
                        <p>Last test: {new Date(it.last_test_at).toLocaleString("hi-IN")} {it.last_test_ok ? "✓" : "✗"}</p>
                      )}
                      {it.last_sync_at && (
                        <p>Last sync: {new Date(it.last_sync_at).toLocaleString("hi-IN")}</p>
                      )}
                    </div>

                    <div className="mt-3 flex flex-wrap gap-2">
                      {it.required_credentials?.length > 0 && (
                        <button
                          onClick={() => { setOpenConfig(it); setCredsInput({}); }}
                          className="text-[12px] px-3 py-1 rounded-full bg-gold text-[#0F172A] font-medium hover:brightness-110"
                          data-testid={`btn-configure-${it.key}`}
                        >
                          Configure
                        </button>
                      )}
                      <button
                        onClick={() => testConnection(it.key)}
                        disabled={testingKey === it.key}
                        className="text-[12px] px-3 py-1 rounded-full border border-gold/40 text-gold hover:bg-gold/10 disabled:opacity-50"
                        data-testid={`btn-test-${it.key}`}
                      >
                        {testingKey === it.key ? <Loader2 className="w-3 h-3 animate-spin inline" /> : "Test"}
                      </button>
                      <button
                        onClick={() => toggleEnabled(it.key, !it.enabled)}
                        className={`text-[12px] px-3 py-1 rounded-full border ${
                          it.enabled ? "border-emerald-500/40 text-emerald-300" : "border-white/20 text-white/60"
                        } hover:bg-white/5`}
                        data-testid={`btn-toggle-${it.key}`}
                      >
                        {it.enabled ? "Enabled" : "Disabled"}
                      </button>
                      {it.docs_url && (
                        <a href={it.docs_url} target="_blank" rel="noopener noreferrer"
                           className="text-[12px] px-3 py-1 rounded-full border border-white/20 text-white/70 hover:bg-white/5 flex items-center gap-1"
                           data-testid={`link-docs-${it.key}`}>
                          Docs <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        ))}
      </div>

      {/* Config Modal */}
      {openConfig && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setOpenConfig(null)}>
          <div className="max-w-lg w-full bg-[#0a1120] border border-gold/30 rounded-2xl p-6" onClick={(e) => e.stopPropagation()} data-testid="config-modal">
            <h3 className="font-heading text-gold text-lg mb-2">{openConfig.display_name_hi}</h3>
            <p className="text-sm text-white/70 mb-4">{openConfig.credentials_help_hi}</p>
            {openConfig.required_credentials.map((k) => (
              <div key={k} className="mb-3">
                <label className="block text-xs text-gold/80 mb-1">{k}</label>
                <textarea
                  rows={k.includes("json") ? 5 : 1}
                  value={credsInput[k] || ""}
                  onChange={(e) => setCredsInput({ ...credsInput, [k]: e.target.value })}
                  placeholder={k.includes("json") ? "Service account JSON..." : "Value..."}
                  className="w-full px-3 py-2 rounded-lg bg-[#0F172A] border border-gold/30 text-white text-sm font-mono focus:border-gold outline-none"
                  data-testid={`config-input-${k}`}
                />
              </div>
            ))}
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setOpenConfig(null)} className="px-4 py-2 rounded-full border border-white/20 text-white/70 text-sm">Cancel</button>
              <button onClick={saveConfig} className="px-4 py-2 rounded-full bg-gold text-[#0F172A] font-semibold text-sm" data-testid="btn-save-config">Save & Activate</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
